<template>
  <div class="watch-dashboard">
    <div class="tri-wrap">
      <WatchTriPane
        :leftHeader="viewMode === 'companies' ? 'Seeds' : 'Unconfirmed'"
        :middleHeader="viewMode === 'companies' ? 'Reports' : 'Review'"
        :companyTitle="companyTitleComputed"
        :seedTitle="seedTitleComputed"
        :enableBack="true"
        :enableLeftSearch="viewMode === 'companies'"
        leftSearchPlaceholder="Search companies…"
        @back="goBack"
        @left-search-change="onLeftSearch"
      >
        <!-- LEFT HEADER TOOLBAR -->
        <template #left-toolbar>
          <!-- Sort (Companies only) -->
          <div v-if="viewMode === 'companies'" class="icon-select">
            <button class="icon-btn dark" aria-label="Sort companies" title="Sort companies">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M3 6h18v2H3zM6 11h12v2H6zM10 16h4v2h-4z" fill="currentColor"/>
              </svg>
            </button>
            <select v-model="companySort" class="native-select overlay">
              <option value="most">Most mentioned</option>
              <option value="last">Last mentioned</option>
              <option value="egregious">Most egregious</option>
            </select>
          </div>

          <!-- Clear selection (Unconfirmed only, when a company is chosen) -->
          <div v-if="viewMode === 'unconfirmed' && !!selectedCompany" class="icon-select">
            <button
              class="icon-btn dark xs"
              @click="onClearUnconfirmedFilters"
              aria-label="Clear selection"
              title="Clear selection"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M7 7H3v4h2V9h8a4 4 0 1 1 0 8H8v2h5a6 6 0 0 0 0-12H7V7z"/>
              </svg>
            </button>
          </div>

          <!-- View mode switch -->
          <div class="icon-select">
            <button class="icon-btn dark" aria-label="Change list type" title="Change list type">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <rect x="3" y="5" width="4" height="4" rx="1" fill="currentColor"/>
                <rect x="9" y="6.5" width="12" height="1.5" rx="0.75" fill="currentColor"/>
                <rect x="3" y="15" width="4" height="4" rx="1" fill="currentColor"/>
                <rect x="9" y="16.5" width="12" height="1.5" rx="0.75" fill="currentColor"/>
              </svg>
            </button>
            <select v-model="viewMode" class="native-select overlay">
              <option value="companies">Watching Companies</option>
              <option value="unconfirmed">Unconfirmed Seeds</option>
            </select>
          </div>
        </template>

        <!-- MIDDLE HEADER TOOLBAR -->
        <template #middle-toolbar>
          <div class="mid-toolbar-wrap">
            <div class="mid-controls one-line">

              <!-- UNCONFIRMED MODE ONLY -->
              <template v-if="viewMode === 'unconfirmed' && selectedCompany">
                <!-- ARMED: hide input; show big green confirm + small red cancel -->
                <div v-if="deleteAllArmed" class="delete-all-row">
                  <button
                    class="delete-all-confirm success"
                    :disabled="isDeletingAll"
                    @click="confirmDeleteAll"
                    aria-label="Confirm delete all seeds"
                  >
                    {{ isDeletingAll
                      ? 'Deleting…'
                      : `Delete ALL ${selectedCompany.companyCanonical || selectedCompany.companyId} Seeds` }}
                  </button>

                  <button
                    class="icon-btn danger xs cancel-arm"
                    :disabled="isDeletingAll"
                    @click="cancelDeleteAll"
                    aria-label="Cancel delete-all"
                    title="Cancel"
                  >
                    <!-- red X -->
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor"
                        d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3 10.6 10.6 16.9 4.3z"/>
                    </svg>
                  </button>
                </div>

                <!-- IDLE: input + confirm + RED trashcan to arm -->
                <template v-else>
                  <label class="sr-only" for="company-input">Company</label>
                  <input
                    id="company-input"
                    class="text-input grow"
                    type="text"
                    v-model="companyInput"
                    :placeholder="selectedCompany?.companyCanonical || 'Enter company name…'"
                    @keydown.enter.prevent="onConfirmCompany"
                    :disabled="!selectedCompany"
                    aria-label="Company name"
                  />

                  <div class="actions">
                    <button
                      v-if="isEdited"
                      class="icon-btn dark xs"
                      @click="onResetCompanyInput"
                      :disabled="!selectedCompany"
                      aria-label="Reset"
                      title="Reset"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor" d="M7 7H3v4h2V9h8a4 4 0 1 1 0 8H8v2h5a6 6 0 0 0 0-12H7V7z"/>
                      </svg>
                    </button>

                    <button
                      class="icon-btn primary xs"
                      @click="onConfirmCompany"
                      :disabled="!selectedCompany || (!selectedSeed && !(selectedCompany?.relatedSeeds?.length)) || isAnyConfirmPending"
                      aria-label="Confirm company"
                      title="Confirm"
                    >
                      <svg v-if="!isAnyConfirmPending" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"/>
                      </svg>
                      <span v-else class="spinner" aria-hidden="true"></span>
                    </button>

                    <!-- RED TRASHCAN (arm delete-all) -->
                    <button
                      class="icon-btn danger xs"
                      :disabled="!(selectedCompany?.relatedSeeds?.length)"
                      @click="armDeleteAll"
                      aria-label="Arm delete all"
                      title="Delete all seeds for this company"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor"
                          d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"/>
                      </svg>
                    </button>
                  </div>
                </template>
              </template>

              <!-- COMPANIES MODE: nothing special -->
              <template v-else-if="viewMode === 'companies' && selectedCompany">
                <div class="spacer"></div>
              </template>

            </div>
          </div>
        </template>




        <!-- LEFT LIST -->
        <template #left>
          <ListViewBuilder
            :items="leftListItems || []"
            :mode="'left'"
            :itemType="'case'"
            :showSorter="false"
            :showsDate="true"
            @itemClick="onPickLeftItem"
          />
        </template>

        <!-- MIDDLE EMPTY -->
        <template #middle-empty>
          <div class="empty-box">Choose a Company to Review</div>
        </template>

        <!-- MIDDLE LIST -->
        <template #middle>
          <ListViewBuilder
            :items="middleListItems || []"
            :mode="'right'"
            :itemType="'chat'"
            :showSorter="false"
            :showsDate="true"
            @itemClick="onPickSeed"
          />
        </template>

        <!-- RIGHT TOOLBAR -->
        <template #right-toolbar>
          <div v-if="selectedSeed" class="right-toolbar-row">
            <!-- IDLE: Fetch Chat + red trashcan (both modes) -->
            <template v-if="!deleteOneArmed">
              <button class="btn-slim solid grow" :disabled="isFetchingChat" @click="onFetchChat">
                {{ isFetchingChat ? 'Fetching…' : 'Fetch Chat' }}
              </button>

              <button class="icon-btn danger xs" :disabled="!selectedSeed" @click="armDeleteOne" title="Delete this seed">
                <!-- trashcan -->
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor"
                    d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"/>
                </svg>
              </button>
            </template>

            <!-- ARMED: big green confirm + red X cancel (both modes) -->
            <template v-else>
              <button class="delete-one-confirm success grow" :disabled="isDeletingOne" @click="confirmDeleteOne">
                {{ isDeletingOne ? 'Deleting…' : (viewMode === 'companies' ? 'Delete THIS Watchseed' : 'Delete THIS Unconfirmed Seed') }}
              </button>
              <button class="icon-btn danger xs" :disabled="isDeletingOne" @click="cancelDeleteOne" title="Cancel">
                <!-- red X -->
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor"
                    d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3 10.6 10.6 16.9 4.3z"/>
                </svg>
              </button>
            </template>
          </div>
        </template>


        <!-- RIGHT DETAIL -->
        <template #right>
          <div v-if="selectedSeed" class="detail-card">
            <div class="detail-content">
              <p><strong>Category:</strong> {{ selectedSeed.likelyLegalCategory }}</p>
              <p><strong>Severity:</strong> {{ selectedSeed.severityScore }}</p>
              <p><strong>Sentiment:</strong> {{ selectedSeed.sentimentScore }}</p>
              <p><strong>Last Updated:</strong> {{ prettyDate(selectedSeed.lastUpdated) }}</p>
              <hr class="divider" />
              <p><strong>Summary:</strong> {{ selectedSeed.issueSummary }}</p>
            </div>
          </div>

          <hr class="divider" />
          <div class="chat-wrap">
            <h4 class="chat-title">Chat</h4>
            <div v-if="isFetchingChat" class="chat-empty">Loading chat…</div>
            <div v-else-if="chatData">
              <p class="chat-meta">Messages: {{ chatData.messages.length }}</p>
              <div class="chat-list">
                <div v-for="m in chatData.messages" :key="m.msgId" class="chat-line">
                  <span class="chat-time">{{ new Date(m.sentStamp).toLocaleString() }}</span>
                  <span class="chat-sender">{{ m.senderId }}</span>
                  <span class="chat-text">{{ m.chatMsgStr }}</span>
                </div>
              </div>
            </div>
            <div v-else class="chat-empty">No chat loaded. Use “Fetch Chat”.</div>
          </div>
        </template>

        <!-- RIGHT EMPTY -->
        <template #right-empty>
          <div v-if="!middleActive" class="standin-card">
            <div class="blk w-60"></div>
            <div class="blk w-90"></div>
            <div class="blk w-75"></div>
          </div>
          <div v-else class="right-hint muted empty-box">
            Select a report from the middle column.
          </div>
        </template>
      </WatchTriPane>

      <LoadingCover :show="isLoading" message="Loading watchlist…" />
    </div>
  </div>
</template>


<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import WatchTriPane from '@/components/WatchTriPane.vue';
import ListViewBuilder from '@/components/ListViewBuilder.vue';
import LoadingCover from '@/components/LoadingCover.vue';
import {
  fetchUnsubmittedWatchSeeds,
  fetchWatchingCosArray,
  watchWatchingCompanies,
  watchCompanySeeds,
  confirmCompanyAndAppendSeeds,
  deleteWatchSeeds, 
  deleteUnconfirmedSeeds 
} from '@/firebase/firestore';
import { fetchAndDecodeChat } from '@/firebase/encrypt';


export default {
  name: 'WatchDashboard',
  components: { WatchTriPane, ListViewBuilder, LoadingCover },
  setup() {
    const router = useRouter();

    // loader
    const isLoading = ref(true);

    // view toggle
    const viewMode = ref('companies');

    // search/sort
    const companySearch = ref('');
    const companySort = ref('most');

    // data
    const watchingCos = ref([]);
    const unsubmittedSeeds = ref([]);

    // selections
    const selectedCompany = ref(null);
    const selectedSeed = ref(null); // declared early (fixes init-order issues)
    const selectedUnconfirmedSeed = ref(null);

    // confirm controls
    const companyInput = ref('');

    // derived: company title (header)
    const companyTitleComputed = computed(
      () => (selectedCompany.value && selectedCompany.value.companyCanonical) || ''
    );

    // derived: seed title (header)
    const seedTitleComputed = computed(() => {
      const s = selectedSeed.value;
      if (!s) return '';
      return s.issueSummary || s.llfcid || s.id || '';
    });

    // pretty date
    const prettyDate = (d) => (d ? new Date(d).toLocaleString() : '');

    const companies = ref([]); // [{ companyId, companyCanonical, totalSeeds, lastMentionedAt, maxSeverity }]
    let unsubWatching = null;

    const companySeeds = ref([]);           // 👈 seeds for selected company (middle col)
    let unsubSeedWatch = null;    

    /* -------------------------- lists: companies mode -------------------------- */

    const originalCompanyName = computed(() =>
      selectedCompany.value?.companyCanonical ||
      selectedCompany.value?.companyId ||
      ''
    );

    const isEdited = computed(() =>
      (companyInput.value || '').trim() !== (originalCompanyName.value || '').trim()
    );

    const middleActive = computed(() => !!selectedCompany.value);

    const filteredCompanies = computed(() => {
      const q = companySearch.value.trim().toLowerCase();
      return (companies.value || []).filter(c =>
        !q ||
        c.companyCanonical.toLowerCase().includes(q) ||
        c.companyId.toLowerCase().includes(q)
      );
    });

    const sortedCompanies = computed(() => {
      const list = [...filteredCompanies.value];
      if (companySort.value === 'most') {
        list.sort((a, b) => (b.totalSeeds || 0) - (a.totalSeeds || 0));
      } else if (companySort.value === 'last') {
        list.sort((a, b) => new Date(b.lastMentionedAt) - new Date(a.lastMentionedAt));
      } else if (companySort.value === 'egregious') {
        list.sort((a, b) => (b.maxSeverity || 0) - (a.maxSeverity || 0) || (b.totalSeeds || 0) - (a.totalSeeds || 0));
      }
      return list;
    });

    const companyListItems = computed(() =>
      sortedCompanies.value.map(c => ({
        caseTitle: c.companyCanonical,
        caseStatus: '',
        lastUpdated: c.lastMentionedAt,
        _raw: c,
      }))
    );

    /* ------------------------ lists: unconfirmed mode ------------------------- */

    const leftListItems = computed(() => {
      if (viewMode.value === 'companies') return companyListItems.value;

      const known = new Set(companies.value.map(c => c.companyCanonical.toLowerCase()));
      const grouped = {};
      for (const s of unsubmittedSeeds.value) {
        const company = (s.companyMentioned?.[0] || 'Unknown').trim();
        if (!company || known.has(company.toLowerCase())) continue;
        (grouped[company] ||= []).push(s);
      }

      return Object.entries(grouped).map(([company, seeds]) => ({
        caseTitle: company,
        caseStatus: `${seeds.length} related seed${seeds.length > 1 ? 's' : ''}`,
        lastUpdated: seeds[0].lastUpdated,
        _raw: { companyId: company, companyCanonical: company, relatedSeeds: seeds },
      }));
    });

    const middleListItems = computed(() => {
      // Nothing until a company/suggestion is selected
      if (!selectedCompany.value) return [];

      if (viewMode.value === 'companies') {
        // LIVE seeds from Firestore for the selected watching company
        return (companySeeds.value || []).map(s => ({
          clientName: s.issueSummary || 'Issue',
          chatTopic: s.likelyLegalCategory || '',
          lastUpdated: s.lastUpdated,
          _raw: s,
          _onFetch: () => fetchChatForSeed(s),
        }));
      }

      // Unconfirmed mode: use the grouped suggestion’s related seeds
      return (selectedCompany.value.relatedSeeds || []).map(s => ({
        clientName: s.issueSummary || 'Issue',
        chatTopic: s.likelyLegalCategory || '',
        lastUpdated: s.lastUpdated,
        _raw: s,
        _onFetch: () => fetchChatForSeed(s),
      }));
    });

    /* ----------------------------- interactions ------------------------------ */

    const onClearUnconfirmedFilters = () => {
      // Clear the “unconfirmed” working state
      companyInput.value = '';
      selectedSeed.value = null;
      selectedUnconfirmedSeed.value = null;
      selectedCompany.value = null;        // deselect the suggested company
      console.log('[UI] Cleared unconfirmed selection & input');
    };

    // const onPickLeftItem = ({ item }) => {
    //   if (!item) return;
    //   const raw = item._raw;
    //   if (!raw) return;

    //   const companyId = raw.companyId ?? raw.companyCanonical ?? raw.id ?? item.caseTitle ?? 'unknown';
    //   const companyCanonical = raw.companyCanonical ?? raw.companyId ?? raw.id ?? item.caseTitle ?? 'Unknown';

    //   selectedCompany.value = { companyId, companyCanonical, relatedSeeds: raw.relatedSeeds ?? [] };
    //   selectedSeed.value = null;
    //   selectedUnconfirmedSeed.value = null;
    // };

    const onPickLeftItem = ({ item }) => {
      if (!item || !item._raw) return;
      const raw = item._raw;

      const companyId        = raw.companyId ?? raw.companyCanonical ?? raw.id ?? item.caseTitle ?? 'unknown';
      const companyCanonical = raw.companyCanonical ?? raw.companyId ?? raw.id ?? item.caseTitle ?? 'Unknown';

      // Preserve relatedSeeds if this came from Unconfirmed
      selectedCompany.value = {
        companyId,
        companyCanonical,
        relatedSeeds: raw.relatedSeeds ?? [],   // <-- keep these for middle list
      };

      // Clear current middle/right selections
      selectedSeed.value = null;
      selectedUnconfirmedSeed.value = null;
      companySeeds.value = [];

      // Only watch Firestore seeds when in Companies mode
      if (unsubSeedWatch) { unsubSeedWatch(); unsubSeedWatch = null; }
      if (viewMode.value === 'companies') {
        unsubSeedWatch = watchCompanySeeds(companyId, (rows) => {
          companySeeds.value = rows;
          // optional: console.log('companySeeds rows:', rows.length);
        });
      }
    };

    const onPickSeed = ({ item }) => {
      const s = item?._raw;
      if (!s) return;
      selectedSeed.value = s;           // right pane detail can show
      selectedUnconfirmedSeed.value = s;
    };

    const goBack = () => router.back();
    const onLeftSearch = (term) => { companySearch.value = term || ''; };

    // keep the middle toolbar input in sync with the selection/view
    // watch([selectedCompany, () => viewMode.value], () => {
    //   companyInput.value =
    //     viewMode.value === 'unconfirmed'
    //       ? (selectedCompany.value?.companyCanonical || selectedCompany.value?.companyId || '')
    //       : '';
    // }, { immediate: true });

    watch([selectedCompany, () => viewMode.value], () => {
      companyInput.value =
        viewMode.value === 'unconfirmed' ? originalCompanyName.value : '';
    }, { immediate: true });

    watch([selectedCompany, selectedSeed, viewMode], () => {
      deleteAllArmed.value = false;
      deleteOneArmed.value = false;
    });

    const onResetCompanyInput = () => {
      companyInput.value = originalCompanyName.value || '';
      console.log('[UI] Reset company input to:', companyInput.value);
    };

    const isConfirming = ref(false);

    const pendingConfirms = ref(new Set());
    const markPending = (id, yes) => {
      const s = new Set(pendingConfirms.value);
      yes ? s.add(id) : s.delete(id);
      pendingConfirms.value = s;
    };

    // ✅ you’re missing this:
    const isAnyConfirmPending = computed(() => pendingConfirms.value.size > 0);

    // Local list ops — adjust to your state shape
    function locallyRemoveFromUnconfirmed(id) {
      unsubmittedSeeds.value = (unsubmittedSeeds.value || []).filter(s => (s.id || s.origChatId) !== id);
    }
    function restoreToUnconfirmed(seed) {
      unsubmittedSeeds.value = [seed, ...(unsubmittedSeeds.value || [])];
    }

    // Toasts / messages
    function showError(msg) { console.error(msg); }

    const onConfirmCompany = async () => {
      const company = selectedCompany.value;
      if (!company) return;

      const usingName =
        (companyInput.value || company.companyCanonical || company.companyId || '').trim();

      const seedsToAppend = selectedSeed.value
        ? [selectedSeed.value]
        : (company.relatedSeeds || []);
      if (!seedsToAppend.length) return;

      // mark pending + optimistic remove
      const seedIds = seedsToAppend.map(s => s.id || s.origChatId);
      seedIds.forEach(id => markPending(id, true));
      seedsToAppend.forEach(s => locallyRemoveFromUnconfirmed(s.id || s.origChatId));

      try {
        await confirmCompanyAndAppendSeeds({
          companyCanonical: usingName,
          companyId: company.companyId,
          seeds: seedsToAppend,
        });

        // ✅ SUCCESS: immediate UI reset back to Unconfirmed with nothing selected
        // stop all spinners
        pendingConfirms.value = new Set();
        // reset inputs/selections
        onClearUnconfirmedFilters();    // clears companyInput + selected* (you already wrote this)
        // ensure we’re on the unconfirmed tab
        viewMode.value = 'unconfirmed';

        // (optional) toast
        // showSuccess('Company confirmed and seeds appended.');

      } catch (e) {
        // rollback optimistic changes
        seedsToAppend.forEach(s => restoreToUnconfirmed(s));
        seedIds.forEach(id => markPending(id, false));
        showError('Could not confirm. Please try again.');
      }
    };


    const onClickDeleteSeed = async () => {
      const seed = selectedSeed.value;
      if (!seed) return;

      const seedId = seed.id || seed.origChatId;
      isDeletingOne.value = true;

      try {
        if (viewMode.value === 'companies') {
          // CONFIRMED: delete under admin/watch/watching/{companyId}/seeds/{seedId}
          const companyId = selectedCompany.value?.companyId;
          if (!companyId) return;

          // optimistic UI
          companySeeds.value = (companySeeds.value || [])
            .filter(s => (s.id || s.origChatId) !== seedId);
          selectedSeed.value = null;

          await deleteWatchSeeds(companyId, seedId);
        } else {
          // UNCONFIRMED: delete under admin/watch/watchlist/{seedId}
          unsubmittedSeeds.value = (unsubmittedSeeds.value || [])
            .filter(s => (s.id || s.origChatId) !== seedId);
          selectedSeed.value = null;

          await deleteUnconfirmedSeeds(seedId);
        }
      } catch (e) {
        console.error('Delete failed:', e);
        alert('Failed to delete. Please try again.');
        // (optional) rollback logic here if you want strong consistency
      } finally {
        isDeletingOne.value = false;
        deleteOneArmed.value = false;
      }
    };



    const onDeleteAllCompanySeeds = async () => {
      const companyId = selectedCompany.value?.companyId;
      const ids = (companySeeds.value || []).map(s => s.id || s.origChatId);
      if (!companyId || ids.length === 0) return;

      if (!confirm(`Delete all ${ids.length} seeds for ${selectedCompany.value.companyCanonical || companyId}?`))
        return;

      try {
        // optimistic clear
        companySeeds.value = [];
        selectedSeed.value = null;
        await deleteWatchSeeds(companyId, ids); // array ok
      } catch (e) {
        console.error('Bulk delete failed:', e);
        alert('Failed to delete all. Please try again.');
      }
    };

    /* ---------------------------- fetch chat stream --------------------------- */

    const isFetchingChat = ref(false);
    const chatData = ref(null);

    const onFetchChat = async () => {
      if (!selectedSeed.value) return;
      isFetchingChat.value = true;
      chatData.value = null;

      try {
        const ownerUid = selectedSeed.value.llfcid || selectedSeed.value.clientId;
        const chatId   = selectedSeed.value.origChatId || selectedSeed.value.id;

        const messages = await fetchAndDecodeChat(ownerUid, chatId, { max: 500 });

        chatData.value = { messages }; // store decoded messages for the UI
        console.log(`[chat] decoded ${messages.length} messages for chat=${chatId}`);
      } catch (err) {
        console.error('Fetch/decode chat failed:', err);
      } finally {
        isFetchingChat.value = false;
      }
    };

    // const fetchChatForSeed = async (seed) => {
    //   const clientId = seed.llfcid || seed.clientId;
    //   const chatId = seed.origChatId || seed.id;
    //   try {
    //     const count = await fetchChatStreamCount(clientId, chatId);
    //     console.log(`[stream] client=${clientId} chat=${chatId} count=${count}`);
    //     // Later: decode + render stream in right pane
    //   } catch (err) {
    //     console.error('Failed to fetch chat stream count:', err);
    //   }
    // };

    const fetchChatForSeed = async (seed) => {
      const ownerUid = seed.llfcid || seed.clientId;
      const chatId   = seed.origChatId || seed.id;
      const messages = await fetchAndDecodeChat(ownerUid, chatId, { max: 500 });
      return messages;
    };

    /* --------------------------------- load ---------------------------------- */
    const simulateInitialLoad = async () => {
      await new Promise(res => setTimeout(res, 300)); // tiny delay for cover
      isLoading.value = false;
    };

    // onMounted(async () => {
    //   simulateInitialLoad();
    //   try {
    //     const [watching, { items }] = await Promise.all([
    //       fetchWatchingCompanies(),
    //       fetchUnsubmittedWatchSeeds({ pageSize: 500 }),
    //     ]);
    //     watchingCos.value = watching;
    //     unsubmittedSeeds.value = items;
    //     console.log('Watching list:', watching);
    //     console.log('Unsubmitted seeds:', items.length);
    //   } catch (e) {
    //     console.error('Failed to fetch watch dashboard data', e);
    //   }
    // });

    onMounted(async () => {
      simulateInitialLoad();
      try {
        // quick list (array of names) from admin/watch
        fetchWatchingCosArray().then(list => {
          watchingCos.value = list;
          console.log('watchingCos array:', list);
        });

        // live companies list from admin/watch/watching
        unsubWatching = watchWatchingCompanies((docs) => {
          // Map Firestore -> your view model fields used elsewhere
          companies.value = docs.map(d => ({
            companyId: d.companyId || d.id,
            companyCanonical: d.companyCanonical || d.id,
            totalSeeds: Number(d.seedCount || 0),
            lastMentionedAt: d.lastMentionedAt || null,
            maxSeverity: Number(d.maxSeverity || 0),
          }));
          console.log('watching companies (live):', companies.value.length);
        });

        // unconfirmed seeds as before
        const { items } = await fetchUnsubmittedWatchSeeds({ pageSize: 500 });
        unsubmittedSeeds.value = items;
        console.log('Unsubmitted seeds:', items.length);
      } catch (e) {
        console.error('Failed to fetch watch dashboard data', e);
      }
    });

    onBeforeUnmount(() => { 
      if (unsubWatching) unsubWatching(); 
      if (unsubSeedWatch) unsubSeedWatch();
    });

    // delete-arm flags
    const deleteAllArmed = ref(false);
    const deleteOneArmed = ref(false);

    // spinners
    const isDeletingAll = ref(false);
    const isDeletingOne = ref(false);

    const armDeleteAll = () => {
      // allow arming in either mode:
      if (viewMode.value === 'unconfirmed') {
        if ((selectedCompany.value?.relatedSeeds?.length || 0) > 0) deleteAllArmed.value = true;
      } else {
        if ((companySeeds.value?.length || 0) > 0) deleteAllArmed.value = true;
      }
    };
    const cancelDeleteAll = () => { deleteAllArmed.value = false; };

    const confirmDeleteAll = async () => {
      if (!selectedCompany.value) return;
      isDeletingAll.value = true;

      try {
        if (viewMode.value === 'companies') {
          // delete watch/{companyId}/seeds/*
          const ids = (companySeeds.value || []).map(s => s.id || s.origChatId);
          if (!ids.length) return;
          // optimistic UI
          companySeeds.value = [];
          selectedSeed.value = null;
          await deleteWatchSeeds(selectedCompany.value.companyId, ids); // your bulk deleter
        } else {
          // UNCONFIRMED: "delete all suggested seeds" for this company
          // If you actually want to DELETE from Firestore watch/* here, you need
          // a mapping from these suggestions to watch doc IDs. Otherwise, discard
          // them locally or call a specific API, e.g. deleteUnsubmittedSeeds(ids).
          const ids = (selectedCompany.value.relatedSeeds || []).map(s => s.id || s.origChatId);
          // optimistic (remove them from the unsubmitted list + clear selection)
          unsubmittedSeeds.value = (unsubmittedSeeds.value || [])
            .filter(s => !ids.includes(s.id || s.origChatId));
          selectedCompany.value = null;
          selectedSeed.value = null;

          // TODO: if you persist unconfirmed seeds, call your backend here:
          // await deleteUnsubmittedSeeds(ids)
        }
      } finally {
        isDeletingAll.value = false;
        deleteAllArmed.value = false;
      }
    };

    // —— Delete ONE (seed) ——
    const armDeleteOne = () => {
      if (selectedSeed.value) deleteOneArmed.value = true;
    };
    const cancelDeleteOne = () => { deleteOneArmed.value = false; };

    const confirmDeleteOne = async () => {
      if (!selectedCompany.value || !selectedSeed.value) return;
      isDeletingOne.value = true;
      try {
        await onClickDeleteSeed();            // you already have this function
      } finally {
        isDeletingOne.value = false;
        deleteOneArmed.value = false;
      }
    };

    return {

      isLoading,

      viewMode, companySort, companySearch, onLeftSearch,

      leftListItems, middleListItems, onPickLeftItem, onPickSeed, onClearUnconfirmedFilters,

      selectedCompany, selectedSeed, selectedUnconfirmedSeed,

      companyTitleComputed, seedTitleComputed, middleActive,

      prettyDate, goBack,

      companyInput, onResetCompanyInput, onConfirmCompany, onClickDeleteSeed, onDeleteAllCompanySeeds, 
      
      deleteAllArmed, deleteOneArmed,
      isDeletingAll, isDeletingOne,
      armDeleteAll, cancelDeleteAll, confirmDeleteAll,
      armDeleteOne,  cancelDeleteOne,  confirmDeleteOne,

      isConfirming, isAnyConfirmPending,

      originalCompanyName, isEdited,

      isFetchingChat, chatData, onFetchChat,
    };
  },
};
</script>

<style scoped>
/* ─── Layout shell ─────────────────────────────────────────────── */
.watch-dashboard {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
}

:root {
  --topbar-h: 0px;
}

.tri-wrap {
  position: relative;
  height: calc(100vh - var(--topbar-h));
  overflow: hidden;
  background: #f8fafc;
  border-radius: 12px;
}

/* ─── Icon-only dropdown + native select overlay ───────────────── */
.icon-select {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  cursor: pointer;
}

.icon-btn.dark {
  background: #0f172a;
  color: #fff;
  border-color: #0b1b35;
}

.icon-btn.dark:hover {
  background: #132241;
}

.native-select.overlay {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* ─── Small list buttons inside cards ──────────────────────────── */
:deep(.btn-slim) {
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

:deep(.btn-slim.solid) {
  background: #0f172a;
  border-color: #0b1b35;
  color: #fff;
}

:deep(.btn-slim.solid:hover) {
  background: #132241;
}

:deep(.btn-slim.wide) {
  width: 100%
}

/* ─── Empty & placeholder boxes ────────────────────────────────── */
.empty-box {
  width: 100%;
  height: 100%;
  min-height: 240px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  font-weight: 500;
}

.placeholder-card {
  height: 100%;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  background: #f3f4f6;
  display: grid;
  place-items: center;
  color: #6b7280;
  padding: 16px;
  text-align: center;
}

.placeholder-title {
  font-weight: 600;
}

/* ─── Middle header / toolbar (single source of truth) ─────────── */
.mid-toolbar-wrap {
  width: 100%;
  min-height: 60px;
  /* header row height */
  padding: 0 8px;
}

/* Keep host header from clipping */
:deep(.pane-header),
:deep(.pane-header .toolbar),
:deep(.pane-middle .toolbar) {
  overflow: visible;
  align-items: center;
  min-height: 56px;
}

/* The control bar itself */
.mid-controls {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mid-controls.hint {
  color: #6b7280;
  font-size: 14px;
  max-width: 760px;
  margin: 0 auto;
}

.mid-controls.one-line {
  max-width: 760px;
  /* center to feed width */
  margin: 0 auto;
  overflow: visible;
  /* don't clip buttons */
}

/* Input flexes; reserves space for 3 icon buttons */
.text-input.grow {
  flex: 1 1 auto;
  min-width: 0;
  /* allow shrinking */
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  background: #fff;
  /* 3×40px buttons + two 8px gaps = 152px kept free on tiny widths */
  flex-basis: calc(100% - 152px);
}

/* Compact icon buttons (reset/delete/confirm) */
.icon-btn.xs {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  cursor: pointer;
  flex: 0 0 auto;
  /* never grow/shrink */
}

.icon-btn.primary {
  background: green;
  color: #fff;
  border-color: green;
}

.icon-btn.primary:hover {
  background: darkgreen;
}

.icon-btn.danger {
  background: #ef4444;
  color: #fff;
  border-color: #b91c1c;
}

.icon-btn.danger:hover {
  background: #dc2626;
}

.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Group for the right-side buttons */
.mid-controls.one-line .actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

/* Screen-reader-only helper */
.sr-only {
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* reuse your slim button look */
:deep(.btn-slim.solid) {
  background: #0f172a;
  border-color: #0b1b35;
  color: #fff;
}

.chat-wrap {
  margin-top: 8px;
  padding: 4px;
}

.chat-title {
  margin: 0 0 6px;
  font-weight: 700;
}

.chat-meta {
  color: #374151;
}

.chat-empty {
  color: #6b7280;
}

.divider {
  border: 0;
  height: 1px;
  background: #e5e7eb;
  margin: 12px 0;
}

/* Neutral stand-in card (no instructions) */
.standin-card {
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  border-radius: 12px;
  padding: 16px;
  display: grid;
  gap: 10px;
}

/* simple grey blocks to suggest content */
.standin-card .blk {
  height: 18px;
  border-radius: 8px;
  background: #cfd4dc;
  opacity: 0.7;
}

.standin-card .blk.w-60 {
  width: 60%;
}

.standin-card .blk.w-75 {
  width: 75%;
}

.standin-card .blk.w-90 {
  width: 90%;
}

/* Optional subtle hint once middle is active */
.right-hint.muted {
  color: #6b7280;
  padding: 12px 8px;
  font-size: 14px;
}

.detail-card {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #111827;
  font-size: 14px;
  line-height: 1.4;
}

.detail-content p {
  margin: 0;
}

.detail-content strong {
  font-weight: 600;
  color: #111827;
}

.mid-controls.one-line>.icon-btn.xs {
  margin-right: 6px;
}

.confirm-delete,
.confirm-delete-row {
  display: inline-flex;
  gap: 8px;
  align-items: center;
}


.right-toolbar-row { display: flex; gap: 8px; width: 100%; }
:deep(.btn-slim.danger){ background:#ef4444;border-color:#b91c1c;color:#fff; }
:deep(.btn-slim.danger:hover){ background:#dc2626; }

.right-toolbar-row { display: flex; align-items: center; gap: 8px; }
.btn-slim.grow { flex: 1 1 auto; }            /* Fetch Chat grows */
.confirm-delete, .confirm-delete-row { display: inline-flex; gap: 8px; align-items: center; }

.delete-all-confirm {
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #b91c1c;
  background: #ef4444;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.delete-all-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

.spacer { flex: 1 1 auto; }

.icon-btn.success { background:#16a34a; border-color:#15803d; color:#fff; }
.icon-btn.success:hover { background:#15803d; }

.delete-all-row {
  display:flex;
  gap:8px;
  width:100%;
}

.delete-all-confirm {
  flex:1 1 auto;            /* expand to fill */
  height:40px;
  border-radius:10px;
  border:1px solid #15803d;
  background:#16a34a;       /* green */
  color:#fff;
  font-weight:700;
  cursor:pointer;
}

.right-toolbar-row {
  display: flex;
  gap: 10px;
  align-items: center;
}
.btn-slim.grow { flex: 1 1 auto; }

/* Icon button color helpers */
.icon-btn.success { background:#16a34a; border-color:#15803d; color:#fff; }
.icon-btn.success:hover { background:#15803d; }
.icon-btn.danger  { background:#ef4444; border-color:#b91c1c; color:#fff; }
.icon-btn.danger:hover { background:#dc2626; }

/* Big green confirm in the middle toolbar (delete all) */
.delete-all-row {
  display: flex;
  gap: 8px;
  width: 100%;
}
.delete-all-confirm {
  flex: 1 1 auto;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #15803d;
  background: #16a34a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}
.delete-all-confirm:disabled { opacity: .6; cursor: not-allowed; }
.cancel-arm { flex: 0 0 40px; }
.spacer { flex: 1 1 auto; }

.right-toolbar-row { display: flex; gap: 10px; align-items: center; }
.grow { flex: 1 1 auto; }

/* Big green confirm button (right column) */
.delete-one-confirm {
  height: 40px;
  border-radius: 10px;
  border: 1px solid #15803d;
  background: #16a34a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}
.delete-one-confirm:disabled { opacity: .6; cursor: not-allowed; }

/* already in your sheet (for reference) */
.icon-btn.danger { background:#ef4444; border-color:#b91c1c; color:#fff; }
.icon-btn.danger:hover { background:#dc2626; }
.btn-slim.solid { background:#0f172a; color:#fff; border-color:#0b1b35; }

/* ─── Responsive ───────────────────────────────────────────────── */
@media (max-width: 640px) {
  .tri-wrap {
    height: 100vh;
  }
}
</style>
